import React, { useEffect, useRef } from 'react';
import { TextField, TextFieldProps } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { useGetTestRailApiSuites } from '../TestRail.hooks';
import { TestRailApiSuite } from '../TestRail.interface';

export interface TestRailApiSuitePickerProps {
  testRailProjectId: number | undefined;
  suite: TestRailApiSuite | null | undefined;
  disabled?: boolean;
  className?: string;
  textFieldProps?: TextFieldProps;
  onChange?: (suite: TestRailApiSuite | null) => void;
}

const TestRailApiSuitePicker: React.FC<TestRailApiSuitePickerProps> = props => {
  const {
    testRailProjectId,
    suite,
    disabled,
    className,
    textFieldProps,
    onChange
  } = props;
  const shouldFetchRef = useRef(true);
  const {
    suites,
    isLoading: isFindingSuites,
    error: getSuiteError,
    getSuites,
    abortGetSuites,
    setState: setSuiteState
  } = useGetTestRailApiSuites();
  const classes: string[] = [];
  className && classes.push(className);

  // when provided project id changes, make sure to refetch
  useEffect(() => {
    shouldFetchRef.current = true;
  }, [testRailProjectId]);

  // cancel ongoing requests when disabled
  useEffect(() => {
    if (!disabled) return;
    abortGetSuites();
    setSuiteState({ isLoading: false });
  }, [disabled, abortGetSuites, setSuiteState]);

  return (
    <Autocomplete
      fullWidth
      disabled={disabled}
      loading={isFindingSuites}
      value={suite || null}
      options={suites || []}
      className={classes.join(' ')}
      // suite prefix: S123
      getOptionLabel={suite => `S${suite.id}: ${suite.name}`}
      getOptionSelected={(option, value) => option.id === value.id}
      onOpen={async () => {
        // initial fetch on open
        if (
          !isFindingSuites &&
          testRailProjectId &&
          (shouldFetchRef.current || !suites || suites.length === 0)
        ) {
          setSuiteState({ isLoading: true });
          const { data } = await getSuites(testRailProjectId);
          shouldFetchRef.current = !data;
        }
      }}
      onInputChange={() => {
        // clear any errors when input changes
        setSuiteState(state => ({ ...state, error: undefined }));
      }}
      onChange={(_, value) => {
        // clear any errors when selection changes
        setSuiteState(state => ({ ...state, error: undefined }));

        onChange?.(value);
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Suite'
          placeholder='Select TestRail Suite'
          {...textFieldProps}
          error={!!getSuiteError || textFieldProps?.error}
          helperText={getSuiteError?.message || textFieldProps?.helperText}
        />
      )}
    />
  );
};

export default TestRailApiSuitePicker;
